import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSageAccess } from '@/hooks/useSageAccess'
import { SageNotConnected } from './SageNotConnected'
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  CardContent,
  DataTable,
  useSorting,
  type DataTableColumn,
  type DataTablePagination,
} from '@atrvd/saas-ui'
import { useFacilities } from '@/hooks/useBanking'
import {
  isAccountBalanceSyncActive,
  isActiveSync,
  useSageBankAccounts,
  useSageSyncStatus,
  useSyncIntacctBalances,
} from '@/hooks/useSageIntegration'
import { useTableColumnPrefs } from '@/hooks/useTableColumnPrefs'
import { formatCurrency, formatDateTime } from '@dcp/shared/utils/formatters'
import type { SageBankAccountRow } from '@/lib/db-tenant'
import { BALANCE_IMPORT_PAGE_SIZE, PAGINATION_VISIBILITY_THRESHOLD } from '@/types/banking'
import type { Facility } from '@/types/banking'

/**
 * Intacct Balances Import table (DCP-159/160).
 *
 * Self-contained container: it reads the Sage bank-account catalog (which now
 * carries the imported `intacct_balance` columns), resolves each account's
 * facility name, and renders one row per account with the balance, sync
 * timestamp, sync status, and a per-account Sync action. A Resync All button
 * in the toolbar triggers a full balance sync.
 *
 * Per-account vs Resync All both go through the single `useSyncIntacctBalances`
 * mutation (the edge contract distinguishes them by the presence of
 * `financial_entity_id`). Sorting and paging are both client-side — the account
 * set is a small per-tenant catalog so no server sort/page endpoint is invented
 * for it (DCP-160 AC.6).
 *
 * DCP-343: sorting moved OUT of the DataTable (`mode: 'server'` now) even
 * though it is still in-memory. DataTable renders exactly the rows it is handed
 * and sorts them internally, so handing it a page slice would sort only that
 * slice — "sort by balance" would order the visible ten rather than the whole
 * catalog. We therefore sort the full set here (via the same `useSorting` hook
 * DataTable uses internally, so behaviour is identical), then slice the page.
 */

/**
 * Local sort-state shape matching the DataTable `sorting.sortState` prop
 * (data-table/types.ts SortState — `{ columnKey, direction }`). Defined
 * inline because the saas-ui barrel exports two `SortState` interfaces and
 * the wrong one wins in the public type — same workaround as
 * OutstandingChecksPage.
 */
interface DataTableSortState {
  columnKey: string
  direction: 'asc' | 'desc' | null
}


/** Display label + Badge variant for each `intacct_balance_status` value. */
function statusBadge(status: string | null): {
  label: string
  variant: 'success' | 'destructive' | 'secondary'
} {
  if (status === 'synced') return { label: 'Loaded', variant: 'success' }
  if (status === 'failed') return { label: 'Failed', variant: 'destructive' }
  // Null / unknown — the account has never had a balance imported.
  return { label: 'Never synced', variant: 'secondary' }
}

/**
 * Resolve, per UPPER-cased Sage location key, WHICH facility owns it.
 *
 * `facilities.sage_location_id` is `TEXT UNIQUE` case-SENSITIVELY and its format
 * CHECK permits mixed case, so `LOC-1` and `loc-1` can be two different
 * facilities while Sage reports a single canonical casing. Every location↔facility
 * comparison in this product is therefore case-INSENSITIVE (DCP-262), and every
 * one of them resolves the resulting collision with the SAME rule: active first,
 * then lowest id — spelled out in the `sba_facility` CTE of
 * `20260730000000_dcp308_facility_id_single_source.sql`
 * (`ORDER BY sba.financial_entity_id, f.is_active DESC, f.id`) and mirrored by
 * `facilitySageLocationIndex` in `db-tenant.ts` and by the sync tasks.
 *
 * Extracted to ONE function (DCP-335) because this file needs the answer twice —
 * for the inactive-facility deny set and for the Facility column's display name —
 * and two hand-copied loops would be free to drift apart, which is exactly the
 * class of inconsistency DCP-335 exists to remove.
 *
 * Facilities with no `sage_location_id` are skipped: they own no key.
 *
 * @param facilities - the full facility list, INCLUDING inactive ones (callers
 *        pass `useFacilities({ activeOnly: false })`) so a deactivated facility's
 *        name still resolves for display. Activity is carried on each winner
 *        rather than filtered out here.
 * @returns key → the winning facility's id, activity flag and display name.
 */
function resolveFacilityWinnersByLocationKey(
  facilities: Facility[],
): Map<string, { id: string; isActive: boolean; name: string }> {
  const winnerByKey = new Map<string, { id: string; isActive: boolean; name: string }>()
  for (const f of facilities) {
    if (!f.sage_location_id) continue
    const key = f.sage_location_id.toUpperCase()
    const isActive = f.is_active === true
    const current = winnerByKey.get(key)
    if (
      current === undefined ||
      (isActive && !current.isActive) ||
      (isActive === current.isActive && f.id < current.id)
    ) {
      winnerByKey.set(key, { id: f.id, isActive, name: f.name })
    }
  }
  return winnerByKey
}

interface IntacctBalanceTableProps {
  /**
   * Optional facility filter: an array of facility UUIDs (from `facilities.id`).
   * When provided and non-empty, only accounts whose `location_id` maps to one
   * of the given facilities are shown. When undefined or empty, all active
   * accounts are shown (current behavior).
   *
   * Mapping chain: facility.id → facility.sage_location_id → account.location_id
   * The Intacct balance is a single current-period value with no date history,
   * so the page-level date filter is intentionally a no-op for this table.
   */
  facilityIds?: string[]
}

export function IntacctBalanceTable({ facilityIds }: IntacctBalanceTableProps) {
  const { isEnabled: sageEnabled, organizationName: sageOrganizationName } = useSageAccess()
  /**
   * Client-side sort state (single-column, mirrors OutstandingChecksPage).
   * Applied here rather than inside the DataTable — see the header note.
   */
  const [sortState, setSortState] = useState<DataTableSortState[]>([])

  /** Active 1-indexed page (DCP-343). */
  const [page, setPage] = useState(1)

  /**
   * Re-sorting reorders the whole catalog, so the rows under the current page
   * number change entirely — land the user on the first page of the new order
   * instead of somewhere in its middle.
   */
  const handleSortChange = useCallback((next: DataTableSortState[]) => {
    setSortState(next)
    setPage(1)
  }, [])

  // Active accounts carry the imported balance columns; facilities resolve the
  // display name. Both are small per-tenant catalogs — full prefetch is
  // cheaper than threading a PostgREST join, same approach as Outstanding
  // Checks.
  const accountsQuery = useSageBankAccounts({ activeOnly: true })
  // Full list (incl. inactive): this is a name-LOOKUP map (location_id → name),
  // not a selector — a row's facility name must still resolve even if that
  // facility was later deactivated (DCP-292). Row visibility is scoped elsewhere.
  const facilitiesQuery = useFacilities({ activeOnly: false })
  const { balances } = useSageSyncStatus()
  const syncBalances = useSyncIntacctBalances()

  const accounts = useMemo(() => accountsQuery.data ?? [], [accountsQuery.data])
  const facilities = useMemo(() => facilitiesQuery.data ?? [], [facilitiesQuery.data])

  /**
   * Resync All is running ONLY when the specific global 'balances'
   * sage_sync_state row is active — NOT when any per-account row is syncing.
   * This is what disables EVERY Sync button (a full resync touches all
   * accounts), independent of per-account locks.
   */
  const resyncAllActive = balances != null && isActiveSync(balances)

  /**
   * Any per-account balance sync currently in flight (across the full,
   * pre-facility-filter account set). Used only to block Resync All while a
   * single-account sync is running — it must NOT disable other per-row Sync
   * buttons.
   */
  const anyAccountActive = accounts.some(isAccountBalanceSyncActive)

  /**
   * The financial_entity_id of the per-account Sync currently being submitted
   * (mutation in flight). Undefined for a Resync All (no financialEntityId) or
   * when idle. Lets us optimistically disable / spin the clicked row before its
   * balance_sync_status flips to 'pending' in the next poll.
   */
  const pendingEntity = syncBalances.isPending
    ? syncBalances.variables?.financialEntityId
    : undefined

  /**
   * Single resolution of "which facility owns this location key", shared by the
   * Facility column's display name and the inactive-facility deny set below.
   * See {@link resolveFacilityWinnersByLocationKey} for the collision rule.
   */
  const facilityWinnerByLocationKey = useMemo(
    () => resolveFacilityWinnersByLocationKey(facilities),
    [facilities],
  )

  /**
   * Lookup map: UPPER-cased Sage location key → facility name (Facility column).
   *
   * DCP-335: keyed and read case-INSENSITIVELY, like every other location
   * comparison in this file and in `db-tenant.ts`. It used to key on the RAW
   * `sage_location_id` and read the RAW `account.location_id`, so a case-variant
   * pair (facility `loc-x`, Sage `LOC-X`) missed and the cell fell back to '—'.
   *
   * That was invisible before DCP-335 — the case-SENSITIVE row filter hid such a
   * row entirely — but this ticket makes the row VISIBLE, which would have left a
   * dash where the facility name belongs, in precisely the scenario the ticket
   * advertises and tests. Same tile/table inconsistency DCP-335 removes, only in
   * the label rather than the number (PResto review 54947, issue #1).
   *
   * Collisions resolve to the WINNING facility's name, so the label now agrees
   * with the facility the money is attributed to everywhere else (deny set, the
   * KPI tile's scope, the `sba_facility` CTE). The previous raw-key behaviour
   * could name one facility while the balance was attributed to another.
   */
  const facilityNameByLocationId = useMemo<Map<string, string>>(() => {
    const m = new Map<string, string>()
    for (const [key, winner] of facilityWinnerByLocationKey) m.set(key, winner.name)
    return m
  }, [facilityWinnerByLocationKey])

  /**
   * Set of Sage location keys (UPPER-cased) that correspond to the selected
   * facility UUIDs.
   *
   * Mapping chain: facilityIds (UUID[]) → facility.sage_location_id (string | null)
   * → account.location_id. The set is empty when no filter is active, which
   * is the signal to skip filtering entirely (show all accounts).
   *
   * DCP-335: keys are UPPER-cased and compared case-INSENSITIVELY, matching the
   * DCP-262 join convention used by `inactiveLocationIds` below, by
   * `intacctLocationScope` in `db-tenant.ts` (which now scopes the Intacct Balance
   * tile) and by the SQL `UPPER()` joins. This match used to be case-SENSITIVE,
   * which silently dropped correctly-mapped rows whenever an admin typed the
   * location in a different case from Sage's canonical value — the exact failure
   * `20260724000000_dcp308_case_insensitive_location_joins.sql` was written for.
   * Left case-sensitive it would now ALSO break the DCP-335 contract, since the
   * tile above this table would count a row the table refuses to show.
   */
  const selectedLocationIds = useMemo<Set<string>>(() => {
    if (!facilityIds || facilityIds.length === 0) return new Set()
    const s = new Set<string>()
    for (const f of facilities) {
      if (facilityIds.includes(f.id) && f.sage_location_id) {
        s.add(f.sage_location_id.toUpperCase())
      }
    }
    return s
  }, [facilityIds, facilities])

  /**
   * Whether the facility filter is actively engaged (facilityIds provided and
   * non-empty). Used to distinguish "no filter → show all" from "filter active
   * but no matching locations → show nothing".
   */
  const isFacilityFilterActive = Boolean(facilityIds && facilityIds.length > 0)

  /**
   * Names of selected facilities that have NO `sage_location_id` configured.
   *
   * Surfaced as a persistent inline advisory above the table so the operator
   * can distinguish two states that otherwise collapse into the same empty
   * message: (1) a legitimate "this facility has 0 Intacct balances" vs (2) a
   * configuration gap — the facility isn't mapped to a Sage location in
   * Settings → Facilities, so it can never show balances here. Mirrors the
   * OutstandingChecksPage advisory for cross-view consistency.
   */
  const facilitiesWithoutSageMapping = useMemo<string[]>(() => {
    if (!facilityIds || facilityIds.length === 0) return []
    return facilities
      .filter((f) => facilityIds.includes(f.id) && !f.sage_location_id)
      .map((f) => f.name)
  }, [facilityIds, facilities])

  /**
   * DCP-292: Sage location keys (UPPER-cased) whose WINNING facility is inactive.
   * An account whose `location_id` resolves to a deactivated facility is hidden
   * from the table (AC2), matching the Intacct tile total which also excludes it.
   * Unmapped accounts (location matches no facility) are kept. Case-insensitive
   * per DCP-262's location↔facility join convention.
   *
   * The winner comes from {@link resolveFacilityWinnersByLocationKey} (active
   * first, then lowest id — PR #183 review), the same rule as
   * `facilitySageLocationIndex` in `db-tenant.ts` and the `sba_facility` CTE in
   * `20260730000000_dcp308_facility_id_single_source.sql`. DCP-335 hoisted that
   * loop out of this memo so the Facility column's name lookup resolves
   * collisions identically instead of keeping a second copy of the rule.
   * Without it, `sage_location_id` being `TEXT UNIQUE` case-SENSITIVELY means
   * `LOC-1` and `loc-1` can be two different facilities, and deactivating the
   * `loc-1` one would upper-case into the same key and hide the ACTIVE `LOC-1`
   * facility's accounts — money disappearing from a facility that is switched on.
   * This table and the tile must agree, so both sides use the identical rule.
   */
  const inactiveLocationIds = useMemo<Set<string>>(() => {
    const s = new Set<string>()
    for (const [key, winner] of facilityWinnerByLocationKey) {
      if (!winner.isActive) s.add(key)
    }
    return s
  }, [facilityWinnerByLocationKey])

  /**
   * Accounts to display after applying the facility filter.
   * DCP-292: accounts under a DEACTIVATED facility are always dropped first
   * (unmapped accounts kept). Then, when no explicit filter is active, the rest
   * pass through; when a filter is active but maps to no Sage locations (e.g. the
   * selected facility has sage_location_id = null), the result is an empty list.
   */
  const activeScopedAccounts = useMemo<typeof accounts>(
    () =>
      accounts.filter(
        (a) => !(a.location_id && inactiveLocationIds.has(a.location_id.toUpperCase())),
      ),
    [accounts, inactiveLocationIds],
  )

  const visibleAccounts = useMemo<typeof accounts>(() => {
    if (!isFacilityFilterActive) return activeScopedAccounts
    return activeScopedAccounts.filter(
      (a) => !!a.location_id && selectedLocationIds.has(a.location_id.toUpperCase()),
    )
  }, [activeScopedAccounts, isFacilityFilterActive, selectedLocationIds])

  /**
   * DCP-292 (Presto #4): the table has accounts, but every one of them belongs to
   * a DEACTIVATED facility — and the user picked no filter. Without this the
   * empty state read "No accounts match the selected facility filter", sending
   * the operator hunting for a filter they never set. Only the inactive-scope
   * step can empty the list here, so name that cause instead.
   */
  const hiddenByInactiveFacilities =
    !isFacilityFilterActive && accounts.length > 0 && activeScopedAccounts.length === 0

  /**
   * Resolve an account's facility display name, or em-dash when unmapped.
   * Case-insensitive since DCP-335 — see `facilityNameByLocationId`.
   */
  const facilityName = useCallback(
    (row: SageBankAccountRow): string =>
      (row.location_id ? facilityNameByLocationId.get(row.location_id.toUpperCase()) : undefined) ??
      '—',
    [facilityNameByLocationId],
  )

  const columns = useMemo<DataTableColumn<SageBankAccountRow>[]>(
    () => [
      {
        key: 'facility',
        header: 'Facility',
        sortable: true,
        render: (row) => facilityName(row),
        // Facility is a joined/derived value with no underlying row field, so
        // the DataTable's default `row[key]` comparator can't sort it. Provide
        // an explicit comparator over the resolved name (case-insensitive).
        sortFn: (a, b, direction) => {
          const cmp = facilityName(a).localeCompare(facilityName(b), undefined, {
            sensitivity: 'base',
          })
          return direction === 'asc' ? cmp : -cmp
        },
      },
      {
        key: 'bank_name',
        header: 'Bank',
        sortable: true,
        render: (row) => row.bank_name ?? '—',
      },
      {
        key: 'bank_account_no',
        header: 'Account Number',
        sortable: true,
        // Row identity column — must always stay visible (DCP-279).
        disableHide: true,
        render: (row) => row.bank_account_no ?? '—',
      },
      {
        key: 'name',
        header: 'Account Name',
        sortable: true,
        render: (row) => row.name,
      },
      {
        key: 'intacct_balance',
        header: 'Balance from Intacct',
        sortable: true,
        align: 'right',
        cellClassName: 'text-right font-medium',
        // Em-dash when the balance has never been imported (AC#5). Negative /
        // zero balances render as-is — no clamping.
        render: (row) =>
          row.intacct_balance == null ? '—' : formatCurrency(row.intacct_balance),
        // PostgREST returns NUMERIC as a string, so the default `row[key]`
        // comparator would sort lexically ("2000.00" before "9.00"). Compare
        // numerically; never-synced (null) rows sort last in both directions.
        sortFn: (a, b, direction) => {
          const na = a.intacct_balance == null ? null : Number(a.intacct_balance)
          const nb = b.intacct_balance == null ? null : Number(b.intacct_balance)
          if (na == null && nb == null) return 0
          if (na == null) return 1
          if (nb == null) return -1
          const cmp = (Number.isFinite(na) ? na : 0) - (Number.isFinite(nb) ? nb : 0)
          return direction === 'asc' ? cmp : -cmp
        },
      },
      {
        key: 'intacct_balance_synced_at',
        header: 'Synchronization Timestamp',
        sortable: true,
        render: (row) =>
          row.intacct_balance_synced_at ? formatDateTime(row.intacct_balance_synced_at) : '—',
      },
      {
        key: 'intacct_balance_status',
        header: 'Synchronization Status',
        sortable: true,
        align: 'center',
        render: (row) => {
          // While this account's own balance sync is in flight, show a
          // transient "Syncing" badge instead of the last data outcome so the
          // operator sees live progress for the row they clicked.
          if (isAccountBalanceSyncActive(row)) {
            return (
              <Badge variant="secondary">
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                Syncing
              </Badge>
            )
          }
          const { label, variant } = statusBadge(row.intacct_balance_status)
          return <Badge variant={variant}>{label}</Badge>
        },
      },
      {
        key: 'action',
        header: 'Action',
        sortable: false,
        align: 'center',
        // The per-row Sync control must always stay visible/actionable (DCP-279).
        disableHide: true,
        render: (row) => {
          // This row's per-account sync is in flight when its own lock is
          // active OR the click that targeted it is still being submitted.
          const rowSyncing =
            isAccountBalanceSyncActive(row) || pendingEntity === row.financial_entity_id
          return (
            <Button
              variant="outline"
              size="sm"
              // Disabled ONLY by: a full Resync All (touches every account), or
              // THIS row's own sync being in flight. A sibling account syncing
              // must NOT disable this button — the backend per-row lock keeps
              // the two independent.
              disabled={resyncAllActive || rowSyncing}
              onClick={() =>
                syncBalances.mutate({ financialEntityId: row.financial_entity_id })
              }
            >
              {rowSyncing ? (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="mr-1 h-3 w-3" />
              )}
              Sync
            </Button>
          )
        },
      },
    ],
    [facilityName, resyncAllActive, pendingEntity, syncBalances],
  )

  // DCP-279: per-user column visibility + order, persisted in localStorage.
  const columnPrefs = useTableColumnPrefs('balance-import-intacct', columns)

  /**
   * Sortable-field descriptors for `useSorting`, derived from `columns` the
   * same way DataTable derives them internally: the column key plus its
   * optional custom `sortFn` (Facility sorts by resolved name, Balance by
   * numeric value). Unlike DataTable we derive them from ALL columns, not just
   * the visible ones, so hiding a column doesn't silently drop the sort that
   * is currently applied.
   */
  const sortableFields = useMemo(
    () =>
      columns
        .filter((column) => column.sortable)
        .map((column) => ({ key: String(column.key), sortFn: column.sortFn })),
    [columns],
  )

  /**
   * `useSorting` keys its sort state by `key`, while the DataTable `sorting`
   * prop uses `columnKey` — the two `SortState` shapes the saas-ui barrel
   * exports (see DataTableSortState above). Translate on the way in.
   */
  const hookSortState = useMemo(
    () => sortState.map((entry) => ({ key: entry.columnKey, direction: entry.direction })),
    [sortState],
  )

  const { sortedData: sortedAccounts } = useSorting<SageBankAccountRow>(
    visibleAccounts,
    sortableFields,
    { mode: 'client', sortState: hookSortState },
  )

  const totalCount = sortedAccounts.length
  const totalPages = Math.max(1, Math.ceil(totalCount / BALANCE_IMPORT_PAGE_SIZE))

  /**
   * The page actually rendered: `page` clamped into the range the CURRENT
   * result set supports. Derived during render rather than written back by an
   * effect (Presto PR #197 finding 1, same fix as ViewBankTransactionsDialog
   * took in PR #184): the accounts and sync-status queries poll and are
   * invalidated by every sync mutation, so rows can vanish from under the user
   * with no click involved. An effect-based clamp lands a frame late — that
   * first render still carries the stale `page`, so the slice returns nothing
   * and the table flashes zero rows while `visibleAccounts` is non-empty. A
   * value derived during render cannot be out of step with the data it came
   * from.
   *
   * DELIBERATE consequence (also mirroring the modal): `page` stays purely the
   * navigation TARGET the user last asked for. If the catalog shrinks
   * transiently and grows back, they return to the page they were on instead
   * of being pinned to whatever the last page was at the shortest moment.
   */
  const effectivePage = Math.min(page, totalPages)

  /**
   * A changed facility filter is a different result set, so page 3 of the old
   * one is meaningless — reset. Keyed on the SORTED ids: `facilityIds` is a
   * fresh array reference on every parent render, and a reordered selection is
   * the same filter, so only a change of membership should reset the page
   * (Presto PR #197 finding 4).
   */
  const facilityFilterKey = facilityIds ? [...facilityIds].sort().join(',') : ''
  useEffect(() => {
    setPage(1)
  }, [facilityFilterKey])

  /** The slice actually handed to the DataTable. */
  const pagedAccounts = useMemo(
    () =>
      sortedAccounts.slice(
        (effectivePage - 1) * BALANCE_IMPORT_PAGE_SIZE,
        effectivePage * BALANCE_IMPORT_PAGE_SIZE,
      ),
    [sortedAccounts, effectivePage],
  )

  /**
   * Pass an explicit pagination config only once the row count clears the
   * threshold; below it DataTable gets `undefined` and renders no control at
   * all (identical to BalanceImportTable).
   */
  const paginationConfig: DataTablePagination | undefined =
    totalCount > PAGINATION_VISIBILITY_THRESHOLD
      ? {
          // The clamped page, so the control never highlights a page the
          // current result set can't fill.
          currentPage: effectivePage,
          totalPages,
          pageSize: BALANCE_IMPORT_PAGE_SIZE,
          totalItems: totalCount,
          onPageChange: setPage,
        }
      : undefined

  const isLoading = accountsQuery.isLoading || facilitiesQuery.isLoading
  const error = accountsQuery.error

  // "Last synced" indicator from the balances sync_state row (mirrors how the
  // checks / bank-accounts views surface last-synced elsewhere).
  const lastSynced = balances?.last_sync_at ? formatDateTime(balances.last_sync_at) : null

  // DCP-368: the tab stays, its contents do not. This table is the SECOND
  // entrance to the Sage balance sync — "Resync all" and the per-entity button
  // both post the same `sync-balances` action as Settings → Sage Intacct — so
  // gating only that page would have left these two buttons live for a tenant
  // with no integration, ending in a 403 toast with nothing to explain it.
  //
  // One return for the whole table rather than disabled buttons: without a
  // connection every row here would be a Sage catalog the tenant does not have.
  if (!sageEnabled) {
    return <SageNotConnected organizationName={sageOrganizationName} />
  }

  return (
    <div className="space-y-4">
      {/* Toolbar: last-synced indicator + Resync All */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground">
          {lastSynced ? `Last synced: ${lastSynced}` : 'Not synced yet'}
        </div>
        <Button
          variant="default"
          size="sm"
          // Disabled while a full resync is active, while ANY per-account sync
          // is in flight (a full resync would collide with it), or while a
          // Resync-All click is being submitted (pendingEntity == null marks
          // the in-flight mutation as the all-accounts flow rather than a
          // single row).
          disabled={
            resyncAllActive ||
            anyAccountActive ||
            (syncBalances.isPending && pendingEntity == null)
          }
          onClick={() => syncBalances.mutate({})}
        >
          {resyncAllActive || (syncBalances.isPending && pendingEntity == null) ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-1 h-4 w-4" />
          )}
          Resync All
        </Button>
      </div>

      {/* Configuration advisory: a selected facility with no Sage Location ID
          can never surface balances here. Persistent inline banner (not a
          toast) so it stays visible while the operator reviews the empty list;
          mirrors OutstandingChecksPage. */}
      {facilitiesWithoutSageMapping.length > 0 && !isLoading && (
        <div
          role="status"
          className="flex flex-col gap-1 rounded-md border border-amber-500/50 bg-amber-50 p-4 text-sm text-amber-900"
        >
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>
              <strong>
                {facilitiesWithoutSageMapping.length === 1
                  ? `${facilitiesWithoutSageMapping[0]} has`
                  : `${facilitiesWithoutSageMapping.length} selected facilities have`}{' '}
                no Sage Location ID configured
              </strong>{' '}
              — their Intacct balances won't appear in this list. Configure the
              mapping in <em>Settings → Facilities</em>.
              {facilitiesWithoutSageMapping.length > 1 && (
                <span className="block mt-1 text-xs opacity-90">
                  Affected facilities: {facilitiesWithoutSageMapping.join(', ')}
                </span>
              )}
            </p>
          </div>
        </div>
      )}

      <div aria-live="polite" aria-busy={isLoading}>
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="flex flex-col items-center gap-3 rounded-md border border-destructive/50 bg-destructive/10 p-6 text-destructive">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              <p className="font-medium">{error.message}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => void accountsQuery.refetch()}>
              Retry
            </Button>
          </div>
        )}

        {/* Data table */}
        {!isLoading && !error && (
          <Card>
            <CardContent className="p-0">
              {visibleAccounts.length === 0 ? (
                <div className="flex min-h-[400px] items-center justify-center">
                  <p className="text-sm text-muted-foreground">
                    {accounts.length === 0
                      ? 'No bank accounts. Run Sync Bank Accounts from Settings → Sage Intacct.'
                      : hiddenByInactiveFacilities
                        ? 'All bank accounts belong to inactive facilities. Reactivate a facility in Settings → Facilities to see its accounts.'
                        : 'No accounts match the selected facility filter.'}
                  </p>
                </div>
              ) : (
                <DataTable<SageBankAccountRow>
                  columns={columns}
                  data={pagedAccounts}
                  rowKey={(row) => row.financial_entity_id}
                  sorting={{
                    // 'server' = "don't sort, I already did" — the rows arrive
                    // pre-sorted AND pre-sliced (DCP-343, see header note).
                    mode: 'server',
                    sortState,
                    onSortChange: handleSortChange,
                  }}
                  pagination={paginationConfig}
                  itemName="accounts"
                  // This table has no `title`/`onExport`, so the Columns menu
                  // must be opted in explicitly (DCP-279, mirrors
                  // OutstandingChecksPage).
                  showColumnControls
                  enableColumnReorder
                  {...columnPrefs}
                />
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
